/**
 * Date formatting utilities for IST (Indian Standard Time)
 */

const IST_OPTIONS = { timeZone: 'Asia/Kolkata' };

/**
 * Format a date string or Date object to IST display format
 * @param {string|Date} date - ISO string or Date object
 * @param {object} options - Additional Intl.DateTimeFormat options
 * @returns {string} Formatted date string in IST
 */
export const formatDateIST = (date, options = {}) => {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';
  
  return d.toLocaleString('en-IN', {
    ...IST_OPTIONS,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  });
};

/**
 * Format date only (no time) in IST
 */
export const formatDateOnlyIST = (date) => {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';
  
  return d.toLocaleDateString('en-IN', {
    ...IST_OPTIONS,
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Format time only in IST
 */
export const formatTimeIST = (date) => {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';
  
  return d.toLocaleTimeString('en-IN', {
    ...IST_OPTIONS,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (date) => {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';
  
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

/**
 * Get current date in IST as YYYY-MM-DD format for input[type="date"]
 * @returns {string} Date string in YYYY-MM-DD format (IST)
 */
export const getTodayIST = () => {
  const now = new Date();
  // Convert to IST by creating a date string in IST timezone
  const istDateString = now.toLocaleDateString('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return istDateString; // Returns YYYY-MM-DD
};

/**
 * Convert a date input value (YYYY-MM-DD) to ISO string with IST timezone
 * Useful for sending date inputs to backend
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} ISO string with current IST time
 */
export const dateInputToISO = (dateString) => {
  if (!dateString) return new Date().toISOString();
  
  // Create date at midnight IST
  const [year, month, day] = dateString.split('-').map(Number);
  const now = new Date();
  
  // Get current time components in IST
  const istTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  
  // Create new date with the input date but current IST time
  const istDate = new Date(year, month - 1, day, istTime.getHours(), istTime.getMinutes(), istTime.getSeconds());
  
  return istDate.toISOString();
};
